import parsePath from "parse-filepath";
import uniqid from "uniqid";
import {
  compressImage,
  IMAGE_COMPRESSION_MAX_SIZE_MB,
} from "tools/file/compressImage";
import type { ProductNoteAttachment } from "ts/api/product";
import type { Extension } from "ts/api/attachmentFile";

/**
 * Anything above this can't be attached. Checked against the compressed file
 * rather than the pick: a photo straight off a phone is several times this
 * before compression and comfortably under it after, so measuring the original
 * would silently reject exactly the uploads compression exists to handle.
 */
const MAX_ATTACHMENT_MB = 5;

/**
 * Turns a freshly-picked file into a note attachment, compressing it on the
 * way in.
 *
 * Both "Attach file(s)" buttons funnel through here — the one that seeds the
 * modal with a first file and the modal's own picker — so no attachment can
 * reach storage uncompressed whichever route the user took. Compressing here
 * rather than at upload time is also what makes the size and extension shown
 * in the list match the bytes that actually get stored.
 *
 * Returns null when the file is still too large once compressed, and for
 * anything `compressImage` leaves alone (documents, GIFs) that was already
 * over the limit.
 */
export const buildNoteAttachment = async (
  file: File
): Promise<ProductNoteAttachment | null> => {
  const compressed = await compressImage(
    file,
    IMAGE_COMPRESSION_MAX_SIZE_MB.orderNotes
  );

  if (compressed.size / (1024 * 1024) >= MAX_ATTACHMENT_MB) {
    return null;
  }

  return {
    id: uniqid(),
    title: compressed.name,
    file: compressed,
    extension: parsePath(compressed.name).ext.slice(1) as Extension,
    size: compressed.size,
    url: "",
  };
};
